import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Case, CaseFilters, PaginatedResponse } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';
import { useEmailVerification } from '../hooks/useEmailVerification';
import { getMedicalRoleLabel } from '../utils/medicalRoles';
import { EmailVerificationOverlay } from '../components/EmailVerificationOverlay';
import { AREAS_OF_INTEREST } from '../../../shared/constants/index.js';

// Use areas of interest from shared constants for the Σύστημα filter
const MEDICAL_SYSTEMS = AREAS_OF_INTEREST;

export function ArchivePage() {
  const { isAuthenticated, user } = useAuth();
  const { requiresVerification, verificationMessage } = useEmailVerification();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false
  });

  const [filters, setFilters] = useState<CaseFilters>({
    page: 1,
    limit: 20,
    status: 'archived',
    sortBy: 'updated_at',
    sortOrder: 'desc'
  });

  const [showEducationalOnly, setShowEducationalOnly] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showVerificationOverlay, setShowVerificationOverlay] = useState(false);

  // Show login prompt for unauthenticated users instead of redirecting
  if (!isAuthenticated) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-6 py-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Αρχείο Περιστατικών</h1>
                <p className="mt-2 text-gray-600">
                  Βιβλιοθήκη επιλυμένων περιστατικών για εκπαιδευτικούς σκοπούς
                </p>
              </div>
              <div className="text-6xl">
                📚
              </div>
            </div>
          </div>

          {/* Login Required Message */}
          <div className="px-6 py-12">
            <div className="text-center">
              <div className="mx-auto w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Σύνδεση Απαιτείται
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Για να δείτε το αρχείο των επιλυμένων περιστατικών, πρέπει να συνδεθείτε στον λογαριασμό σας. 
                Το αρχείο περιέχει πολύτιμο εκπαιδευτικό υλικό από πραγματικά περιστατικά παθολογοανατομίας.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Σύνδεση
                </Link>
                
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Εγγραφή
                </Link>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Τι θα βρείτε στο Αρχείο:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Επιλυμένα Περιστατικά</h4>
                      <p className="text-sm text-gray-600">Περιστατικά με τελικές διαγνώσεις και εκπαιδευτικές σημειώσεις</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Αναζήτηση & Φίλτρα</h4>
                      <p className="text-sm text-gray-600">Εύκολη αναζήτηση περιστατικών ανά ανθρώπινο σύστημα</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Εκπαιδευτικό Υλικό</h4>
                      <p className="text-sm text-gray-600">Συλλογή περιστατικών για συνεχή εκπαίδευση</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fetchArchivedCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams: Record<string, string> = {};
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          // Convert camelCase to snake_case for backend compatibility
          const snakeKey = key === 'sortBy' ? 'sort_by' : 
                          key === 'sortOrder' ? 'sort_order' : key;
          queryParams[snakeKey] = String(value);
        }
      });

      if (searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }

      // Add educational material filter - educational content has 'low' urgency level
      if (showEducationalOnly) {
        queryParams.urgency_level = 'low';
      }

      const searchParams = new URLSearchParams(queryParams);
      const response = await apiClient.get(`/cases?${searchParams}`);

      const data = response.data;
      let cases = data.data.cases;
      
      // Apply client-side sorting as a fallback if backend sorting isn't working
      if (filters.sortBy === 'medical_role') {
        const roleOrder = { 'professor': 4, 'specialist_doctor': 3, 'resident_doctor': 2, 'external_collaborator': 1 };
        cases = [...cases].sort((a, b) => {
          const aRole = a.author?.medicalRole || 'external_collaborator';
          const bRole = b.author?.medicalRole || 'external_collaborator';
          const aValue = roleOrder[aRole as keyof typeof roleOrder] || 0;
          const bValue = roleOrder[bRole as keyof typeof roleOrder] || 0;
          return filters.sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
        });
      } else if (filters.sortBy === 'engagement') {
        cases = [...cases].sort((a, b) => {
          const aCount = a.commentCount || 0;
          const bCount = b.commentCount || 0;
          return filters.sortOrder === 'desc' ? bCount - aCount : aCount - bCount;
        });
      } else if (filters.sortBy === 'updated_at') {
        cases = [...cases].sort((a, b) => {
          const aDate = new Date(a.updatedAt || a.createdAt).getTime();
          const bDate = new Date(b.updatedAt || b.createdAt).getTime();
          return filters.sortOrder === 'desc' ? bDate - aDate : aDate - bDate;
        });
      }
      
      setCases(cases);
      
      // Use proper pagination from API
      setPagination({
        currentPage: data.data.page,
        totalPages: data.data.totalPages,
        totalCount: data.data.totalCount,
        hasMore: data.data.hasNext
      });

    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Σφάλμα φόρτωσης αρχειοθετημένων περιστατικών');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, showEducationalOnly]);

  useEffect(() => {
    fetchArchivedCases();
  }, [fetchArchivedCases]);

  // Update overlay state when verification status changes
  useEffect(() => {
    setShowVerificationOverlay(requiresVerification);
  }, [requiresVerification]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }));
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleFilterChange = (key: keyof CaseFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      status: 'archived',
      sortBy: 'updated_at',
      sortOrder: 'desc'
    });
    setSearchQuery('');
    setShowEducationalOnly(false);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Πριν από λίγο';
    } else if (diffInHours < 24) {
      return `Πριν από ${diffInHours} ώρες`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) {
        return 'Χθες';
      } else if (diffInDays < 7) {
        return `Πριν από ${diffInDays} μέρες`;
      } else {
        return date.toLocaleDateString('el-GR');
      }
    }
  };

  return (
    <div className="relative">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20 ${showVerificationOverlay ? "opacity-30 pointer-events-none" : ""}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Αρχείο Περιστατικών
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {pagination.totalCount} αρχειοθετημένα περιστατικά
          </p>
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="mt-4 sm:mt-0 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          🔍 Φίλτρα & Αναζήτηση
        </button>
      </div>


      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Αναζήτηση στο αρχείο..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400">🔍</span>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Σύστημα
              </label>
              <select
                value={filters.caseType || ''}
                onChange={(e) => handleFilterChange('caseType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Όλα τα συστήματα</option>
                {MEDICAL_SYSTEMS.map(system => (
                  <option key={system} value={system}>{system}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ταξινόμηση
              </label>
              <select
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const parts = e.target.value.split('_');
                  const sortBy = parts.slice(0, -1).join('_');
                  const sortOrder = parts[parts.length - 1];
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('sortOrder', sortOrder);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="updated_at_desc">Πρόσφατα αρχειοθετημένα</option>
                <option value="medical_role_desc">Ανά ρόλο γιατρού (Καθηγητής → Ειδικευόμενος)</option>
                <option value="engagement_desc">Ανά αριθμό απαντήσεων</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Καθαρισμός
              </button>
            </div>
          </div>
          
          {/* Educational Material Checkbox */}
          <div className="border-t pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showEducationalOnly}
                onChange={(e) => {
                  setShowEducationalOnly(e.target.checked);
                  setFilters(prev => ({ ...prev, page: 1 }));
                }}
                className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Εμφάνιση μόνο "Εκπαιδευτικού Υλικού"
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => fetchArchivedCases()}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Προσπάθεια ξανά
          </button>
        </div>
      )}

      {/* Cases Grid */}
      {!loading && !error && (
        <>
          {cases.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Δεν βρέθηκαν αρχειοθετημένα περιστατικά
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || filters.tissueType 
                  ? 'Δοκιμάστε να αλλάξετε τα κριτήρια αναζήτησης.'
                  : 'Δεν υπάρχουν αρχειοθετημένα περιστατικά ακόμα.'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {cases.map((caseItem) => (
                  <Link
                    key={caseItem.id}
                    to={`/cases/${caseItem.id}`}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {caseItem.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {caseItem.caseType}
                            {caseItem.tissueType && ` • ${caseItem.tissueType}`}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Αρχειοθετημένο
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-gray-700 text-sm line-clamp-3 mb-4">
                        {caseItem.description}
                      </p>

                      {/* Patient Info (if available) */}
                      {(caseItem.patientAge || caseItem.patientGender) && (
                        <div className="mb-4 text-sm text-gray-600">
                          <span className="inline-flex items-center">
                            👤{' '}
                            {caseItem.patientAge && `${caseItem.patientAge} ετών`}
                            {caseItem.patientAge && caseItem.patientGender && ', '}
                            {caseItem.patientGender === 'male' && 'Άνδρας'}
                            {caseItem.patientGender === 'female' && 'Γυναίκα'}
                            {caseItem.patientGender === 'other' && 'Άλλο'}
                          </span>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            👁️ {caseItem.viewCount || 0}
                          </span>
                          <span className="flex items-center">
                            💬 {caseItem.comment_count || caseItem.commentCount || 0}
                          </span>
                        </div>
                        <div className="text-right">
                          {(caseItem.first_name || caseItem.author) && (
                            <div className="space-y-1">
                              <p className="font-medium text-gray-900">
                                Δρ. {caseItem.first_name || caseItem.author?.firstName} {caseItem.last_name || caseItem.author?.lastName}
                              </p>
                              {(caseItem.medical_role || caseItem.author?.medicalRole) && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {getMedicalRoleLabel(
                                    caseItem.medical_role || caseItem.author?.medicalRole || 'external_collaborator', 
                                    caseItem.sex || caseItem.author?.sex
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                          <p className="mt-1 text-xs">
                            Δημιουργήθηκε: {formatDate(caseItem.createdAt.toString())}
                          </p>
                          <p className="text-xs text-blue-600">
                            📁 Αρχειοθετήθηκε: {formatDate(caseItem.updatedAt?.toString() || caseItem.createdAt.toString())}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Προηγούμενη
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Επόμενη
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Εμφάνιση{' '}
                        <span className="font-medium">
                          {(pagination.currentPage - 1) * (filters.limit || 20) + 1}
                        </span>{' '}
                        έως{' '}
                        <span className="font-medium">
                          {Math.min(pagination.currentPage * (filters.limit || 20), pagination.totalCount)}
                        </span>{' '}
                        από{' '}
                        <span className="font-medium">{pagination.totalCount}</span> αποτελέσματα
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                        <button
                          onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Προηγούμενη</span>
                          ←
                        </button>
                        
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                page === pagination.currentPage
                                  ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Επόμενη</span>
                          →
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      </div>

      {/* Email Verification Overlay */}
      {showVerificationOverlay && (
        <EmailVerificationOverlay
          actionName="αποκτήσετε πρόσβαση στο αρχείο"
        />
      )}
    </div>
  );
}